import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Layers, Search, LogOut, User, Settings, LayoutDashboard, CalendarDays, History, Users } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS } from "@/lib/constants";
import { NotificationBell } from "@/components/NotificationBell";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

export function TopNav({ search, onSearch }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [term, setTerm] = useState(search || "");

  const initials = (user?.full_name || "U")
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const showPortalNav = user?.role && user.role !== "developer";
  const navLinks = [
    { to: "/", label: "Orders", testid: "nav-orders", icon: LayoutDashboard, active: location.pathname === "/" || location.pathname.startsWith("/orders") },
    { to: "/calendar", label: "Calendar", testid: "nav-calendar", icon: CalendarDays, active: location.pathname === "/calendar" },
    { to: "/history", label: "History", testid: "nav-history", icon: History, active: location.pathname === "/history" },
  ];
  if (user?.role === "manager") {
    navLinks.push({ to: "/team", label: "Manage Team", testid: "nav-team", icon: Users, active: location.pathname === "/team" });
  }

  return (
    <header className="sticky top-0 z-40 border-b border-slate-200 bg-white">
      <div className="mx-auto flex h-16 max-w-7xl items-center gap-3 px-4 sm:px-6">
        <button
          data-testid="nav-logo"
          onClick={() => navigate("/")}
          className="flex items-center gap-2.5"
        >
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-600 text-white">
            <Layers className="h-5 w-5" />
          </div>
          <span className="font-heading text-xl font-extrabold tracking-tight text-slate-900">
            ApparelFlow
          </span>
        </button>

        {showPortalNav && (
          <nav className="ml-3 hidden items-center gap-1 md:flex">
            {navLinks.map((l) => (
              <button
                key={l.to}
                data-testid={l.testid}
                onClick={() => navigate(l.to)}
                className={cn(
                  "flex items-center gap-2 rounded-lg px-3 py-2 text-base font-semibold transition-colors",
                  l.active ? "bg-blue-50 text-blue-700" : "text-slate-600 hover:bg-slate-100"
                )}
              >
                <l.icon className="h-5 w-5" /> {l.label}
              </button>
            ))}
          </nav>
        )}

        {onSearch && (
          <div className="relative ml-2 hidden flex-1 lg:block">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="global-search-input"
              value={term}
              onChange={(e) => {
                setTerm(e.target.value);
                onSearch(e.target.value);
              }}
              placeholder="Search buyer, style, PO, department…"
              className="h-11 w-full rounded-lg border border-slate-200 bg-slate-50 pl-10 pr-4 text-base text-slate-800 outline-none transition-colors placeholder:text-slate-400 focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-100"
            />
          </div>
        )}

        <div className="ml-auto flex items-center gap-1.5">
          <NotificationBell />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="profile-menu-trigger"
                className="flex items-center gap-2.5 rounded-full py-1 pl-1 pr-2 transition-colors hover:bg-slate-100"
              >
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-800 text-sm font-bold text-white">
                  {initials}
                </div>
                <div className="hidden text-left sm:block">
                  <p className="text-sm font-semibold leading-tight text-slate-800">{user?.full_name}</p>
                  <p className="text-xs leading-tight text-slate-500">{ROLE_LABELS[user?.role]}</p>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="text-sm font-semibold text-slate-800">{user?.full_name}</div>
                <div className="text-xs font-normal text-slate-500">{user?.email}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {user?.role === "developer" && (
                <DropdownMenuItem data-testid="nav-developer-portal" onClick={() => navigate("/developer")}>
                  <Settings className="mr-2 h-4 w-4" /> Developer Portal
                </DropdownMenuItem>
              )}
              <DropdownMenuItem disabled>
                <User className="mr-2 h-4 w-4" /> {ROLE_LABELS[user?.role]}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="logout-btn" onClick={logout} className="text-rose-600 focus:text-rose-600">
                <LogOut className="mr-2 h-4 w-4" /> Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
